import classnames from 'classnames';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import '@/entities/event/ui/styles.scss';
import useThumbnailBookmark from './useThumbnailBookmark';

export type ThumbnailBookmarkProps = {
  id: number | string;
  onClickSelect?: () => void;
  // Optional: the useEffect below only calls drawStrip when ecgData?.length > 0,
  // and width/height are otherwise only passed straight through as SVG attrs.
  ecgData?: number[][];
  channels?: number[];
  gain?: number;
  speed?: number;
  width?: number;
  height?: number;
  isSelected?: boolean;
  isActive?: boolean;
  isChanged?: boolean;
  disabled?: boolean;
  className?: string;
  time?: string | number;
  typeRight?: React.ReactNode;
  timezoneOffset?: number;
  startReport?: string | number;
  highPass?: number;
  notes?: string;
  beatMarkers?: number[];
  hesBeatStatus?: (string | number)[];
};

/**
 * Renders one ECG bookmark thumbnail as a selectable button carrying the drawn waveform, its timestamp and any notes. Destructures the display flags from props, hands the SVG canvas ref to useThumbnailBookmark for drawing, and toggles the active, changed and selected modifier classes.
 *
 * @param props - Bookmark identity, sizing, state flags and the timestamp and notes to display.
 * @returns The bookmark thumbnail button with its waveform SVG and corner overlays.
 */
const ThumbnailBookmark = ({
  id,
  onClickSelect,
  ecgData,
  channels,
  gain,
  speed,
  width,
  height,
  isSelected,
  isActive,
  isChanged,
  disabled = false,
  className,
  time,
  typeRight,
  timezoneOffset,
  startReport,
  highPass,
  notes,
  beatMarkers,
  hesBeatStatus,
}: ThumbnailBookmarkProps) => {
  const { canvasRef } = useThumbnailBookmark({
    ecgData,
    channels,
    gain,
    speed,
    width,
    height,
    highPass,
    beatMarkers,
    hesBeatStatus,
  });

  /**
   * Formats a bookmark timestamp as a clock time plus its one-based recording day number. Converts both the bookmark time and the report start into the study timezone, diffs their day boundaries, and appends the day index only when a report start is known.
   *
   * @returns The formatted HH:mm:ss string, optionally followed by the parenthesised day number.
   */
  const renderTime = () => {
    const stripDate = convertToMomentJs(time, timezoneOffset);
    const dateStartReport = convertToMomentJs(startReport, timezoneOffset).startOf('days');
    const stringStartReport = startReport
      ? ` (${stripDate.clone().startOf('days').diff(dateStartReport, 'days') + 1})`
      : '';
    return stripDate.format('HH:mm:ss') + stringStartReport;
  };

  /**
   * Builds the bottom-left overlay of a bookmark thumbnail from its timestamp and notes. Pushes the formatted time and a notes line carrying a hover tooltip, then interleaves divider elements when both pieces are present.
   *
   * @returns An array of nodes for the timestamp, the notes line and any separating dividers.
   */
  const renderBottomLeftCorner = () => {
    let resultHtml: React.ReactNode[] = [];
    if (time) {
      resultHtml.push(<div>{renderTime()}</div>);
    }
    if (notes) {
      resultHtml.push(
        <div id={`${id}-notes`} className="thumbnail-notes">
          {`Notes: ${notes}`}
          <UncontrolledTooltip
            className="custom-tooltip --thumbnail"
            target={`${id}-notes`}
            modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            {notes}
          </UncontrolledTooltip>
        </div>,
      );
    }
    if (resultHtml.length > 1) {
      resultHtml = resultHtml.flatMap((value, index, array) =>
        array.length - 1 !== index ? [value, <div className="divider" />] : value,
      );
    }
    return resultHtml;
  };

  return (
    <button
      id={id as string}
      disabled={disabled}
      className={classnames(
        'single-strip-event',
        className,
        isActive ? '--active' : '',
        isChanged ? '--changed' : '',
        isSelected ? '--selected' : '',
      )}
      onClick={onClickSelect}
    >
      <div className="vertical-line-bookmark" />
      <svg ref={canvasRef} width={width} height={height} />
      <div className="top-left-corner" />
      <div className="line-bottom" />
      <div className="bottom-left-corner">{renderBottomLeftCorner()}</div>
      <div className="bottom-right-corner">
        {typeRight && <div className="strip-right">{typeRight}</div>}
      </div>
    </button>
  );
};

export default ThumbnailBookmark;
